/**
 * Provides shared profile form state for the edit screen and field selection routes.
 */
import type { Profile } from "@spork/s4p-server-sdk";
import {
    useCallback,
    useMemo,
    useState,
    type ReactNode
} from "react";
import { useTranslation } from "react-i18next";

import { log } from "@/logger";
import { usePatchProfileMutation } from "@/query/apiSlice";

import type { StandardProfileFieldKey } from "../config/profileFormGroups";
import {
    buildProfilePatch,
    createProfileFormValues,
    isProfileFormDirty,
} from "../lib/profileFormState";
import { ProfileFormContext } from "./profileFormContext";

type ProfileFormProviderProps = {
    person: Profile;
    readOnly?: boolean;
    children: ReactNode;
};

export function ProfileFormProvider({
    person,
    readOnly = false,
    children,
}: ProfileFormProviderProps) {
    const { t } = useTranslation();
    const [values, setValues] = useState(() => createProfileFormValues(person));
    const [saveError, setSaveError] = useState<string | null>(null);
    const [patchProfile, { isLoading: isSaving }] = usePatchProfileMutation();

    const isDirty =
        !readOnly && isProfileFormDirty(person, values);

    const save = useCallback(async () => {
        setSaveError(null);

        const patch = buildProfilePatch(person, values);
        if (Object.keys(patch).length === 0) {
            return true;
        }

        try {
            await patchProfile({ id: person.id, data: patch }).unwrap();
            return true;
        } catch (error) {
            log.error("patchProfile failed", error);
            const message =
                typeof error === "object" && error && "message" in error
                    ? String((error as { message?: string }).message)
                    : t("profile.saveFailed");
            setSaveError(message);
            return false;
        }
    }, [person, values, patchProfile, t]);

    function updateStandardField(
        key: StandardProfileFieldKey,
        nextValue: string
    ) {
        setValues((current) => ({ ...current, [key]: nextValue }));
    }

    function updateCustomField(
        fieldCode: string,
        nextValue: string | number | number[] | null
    ) {
        setValues((current) => ({
            ...current,
            custom_fields: {
                ...current.custom_fields,
                [fieldCode]: nextValue,
            },
        }));
    }

    const value = useMemo(
        () => ({
            person,
            readOnly,
            values,
            isDirty,
            isSaving,
            saveError,
            updateStandardField,
            updateCustomField,
            save,
        }),
        [person, readOnly, values, isDirty, isSaving, saveError, save]
    );

    return (
        <ProfileFormContext.Provider value={value}>
            {children}
        </ProfileFormContext.Provider>
    );
}
